import React, { useState, useEffect } from 'react';
import LayoutWrapper from '../components/LayoutWrapper';
import {
    GiftIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    CalendarIcon,
    EnvelopeIcon,
    UserIcon,
    CheckCircleIcon,
    DocumentDuplicateIcon,
    CurrencyDollarIcon,
    XMarkIcon,
    BanknotesIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import config from '../config';
import Helper from '../helpers';

const baseUrl = config.apiBaseUrl;

// yyyy-MM-dd for today, used as the earliest allowed first payment date
const todayString = () => new Date().toISOString().slice(0, 10);

// The plan dropdown label, built from the plan's real cycle type and length, e.g. "every 4 weeks" or "every 4 months".
// It used to say "week(s)" for every plan, which hid plans that were saved as months.
export const planCycleText = (cycleType, length) => {
    const units = { 0: 'day', 1: 'week', 2: 'month', 3: 'quarter', 4: 'year' };
    const unit = units[cycleType] || 'period';
    const count = parseInt(length) || 1;
    return count === 1 ? `every ${unit}` : `every ${count} ${unit}s`;
};

// Plain English summary under the "Repeat every" box, e.g. "Charged every 4 weeks"
const describeCycle = (frequency, length) => {
    const units = { Weekly: 'week', Monthly: 'month', Quarterly: 'quarter', Yearly: 'year' };
    const unit = units[frequency] || 'month';
    const count = parseInt(length) || 1;
    return count === 1 ? `Charged every ${unit}` : `Charged every ${count} ${unit}s`;
};

const SubscriptionOffers = () => {
    const [offers, setOffers] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [venueUsers, setVenueUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showExistingUserModal, setShowExistingUserModal] = useState(false);
    const [venueKey, setVenueKey] = useState('');
    const [useCustomSubscription, setUseCustomSubscription] = useState(false);
    const [useCustomForExisting, setUseCustomForExisting] = useState(false);

    // Form state for creating new offer
    const [formData, setFormData] = useState({
        // Existing subscription
        subscriptionId: '',
        // Custom subscription
        customSubscriptionName: '',
        customPrice: '',
        customTokenAmount: '',
        customFrequency: 'Monthly',
        customCycleLength: 1,
        customFirstCollectionDate: '',
        // Common fields
        recipientEmail: '',
        recipientName: '',
        maxUses: 1,
        expiresOn: '',
        notes: '',
        isPrivate: true
    });

    // Form state for existing user offers
    const [existingUserFormData, setExistingUserFormData] = useState({
        selectedUserId: '',
        userEmail: '',
        subscriptionId: '',
        customSubscriptionName: '',
        customPrice: '',
        customTokenAmount: '',
        customFrequency: 'Monthly',
        customCycleLength: 1,
        customFirstCollectionDate: '',
        expiryDays: 7,
        notes: ''
    });

    useEffect(() => {
        const initialize = async () => {
            const venue = await Helper.getUserVenueId();
            setVenueKey(venue);
            await fetchOffers(venue);
            await fetchSubscriptions(venue);
            await fetchVenueUsers(venue);
        };
        initialize();
    }, []);

    const fetchOffers = async (venue) => {
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/SubscriptionOffer/list/${venue}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'success' && data.offers) {
                    setOffers(data.offers);
                }
            }
        } catch (error) {
            console.error('Error fetching offers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSubscriptions = async (venue) => {
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Subscription/getVenueSubscription/${venue}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'success' && data.items) {
                    setSubscriptions(data.items);
                }
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
        }
    };

    const fetchVenueUsers = async (venue) => {
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/users/venueusers/${venue}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setVenueUsers(data || []);
            }
        } catch (error) {
            console.error('Error fetching venue users:', error);
            setVenueUsers([]);
        }
    };

    const handleCreateOffer = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = await Helper.getCookie('wh-jwt');
            
            // Prepare payload based on whether using custom or existing subscription
            const payload = useCustomSubscription ? {
                customSubscription: {
                    name: formData.customSubscriptionName,
                    price: parseFloat(formData.customPrice),
                    tokenAmount: parseInt(formData.customTokenAmount),
                    frequency: formData.customFrequency,
                    cycleLength: parseInt(formData.customCycleLength) || 1,
                    firstCollectionDate: formData.customFirstCollectionDate || null
                },
                recipientEmail: formData.recipientEmail,
                recipientName: formData.recipientName,
                maxUses: parseInt(formData.maxUses),
                expiresOn: formData.expiresOn,
                notes: formData.notes,
                isPrivate: formData.isPrivate
            } : {
                subscriptionId: formData.subscriptionId,
                recipientEmail: formData.recipientEmail,
                recipientName: formData.recipientName,
                maxUses: parseInt(formData.maxUses),
                expiresOn: formData.expiresOn,
                notes: formData.notes,
                isPrivate: formData.isPrivate
            };

            const response = await fetch(`${baseUrl}/api/SubscriptionOffer/create/${venueKey}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setShowCreateModal(false);
                setFormData({
                    subscriptionId: '',
                    customSubscriptionName: '',
                    customPrice: '',
                    customTokenAmount: '',
                    customFrequency: 'Monthly',
                    customCycleLength: 1,
                    customFirstCollectionDate: '',
                    recipientEmail: '',
                    recipientName: '',
                    maxUses: 1,
                    expiresOn: '',
                    notes: '',
                    isPrivate: true
                });
                setUseCustomSubscription(false);
                await fetchOffers(venueKey);
                alert('Subscription offer created successfully!');
            } else {
                const errorText = await response.text();
                alert(`Failed to create subscription offer: ${errorText}`);
            }
        } catch (error) {
            console.error('Error creating offer:', error);
            alert('Error creating subscription offer');
        } finally {
            setLoading(false);
        }
    };

    const handleOfferToExistingUser = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = await Helper.getCookie('wh-jwt');
            
            // Prepare payload based on whether using custom or existing subscription
            const payload = useCustomForExisting ? {
                userEmail: existingUserFormData.userEmail,
                customSubscription: {
                    name: existingUserFormData.customSubscriptionName,
                    price: parseFloat(existingUserFormData.customPrice),
                    tokenAmount: parseInt(existingUserFormData.customTokenAmount),
                    frequency: existingUserFormData.customFrequency,
                    cycleLength: parseInt(existingUserFormData.customCycleLength) || 1,
                    firstCollectionDate: existingUserFormData.customFirstCollectionDate || null
                },
                expiryDays: parseInt(existingUserFormData.expiryDays),
                notes: existingUserFormData.notes
            } : {
                userEmail: existingUserFormData.userEmail,
                subscriptionId: parseInt(existingUserFormData.subscriptionId),
                expiryDays: parseInt(existingUserFormData.expiryDays),
                notes: existingUserFormData.notes
            };

            const response = await fetch(`${baseUrl}/api/SubscriptionOffer/offer-to-existing-user/${venueKey}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setShowExistingUserModal(false);
                setExistingUserFormData({
                    selectedUserId: '',
                    userEmail: '',
                    subscriptionId: '',
                    customSubscriptionName: '',
                    customPrice: '',
                    customTokenAmount: '',
                    customFrequency: 'Monthly',
                    customCycleLength: 1,
                    customFirstCollectionDate: '',
                    expiryDays: 7,
                    notes: ''
                });
                setUseCustomForExisting(false);
                await fetchOffers(venueKey);
                alert('Subscription offer sent successfully! The user will receive an email.');
            } else {
                const errorData = await response.json();
                alert(`Failed to send subscription offer: ${errorData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error sending offer to existing user:', error);
            alert('Error sending subscription offer');
        } finally {
            setLoading(false);
        }
    };

    const copyInviteCode = (code) => {
        navigator.clipboard.writeText(code);
        alert('Invite code copied to clipboard!');
    };

    // Two links exist: new people sign up, people who already have an account accept the offer.
    // The button label flips to "Copied" for a couple of seconds instead of using a popup.
    const [copiedLink, setCopiedLink] = useState('');
    const copyInviteLink = (code, kind) => {
        const path = kind === 'member' ? `/accept-subscription-offer/${code}` : `/signup/invite/${code}`;
        navigator.clipboard.writeText(`${window.location.origin}${path}`);
        setCopiedLink(`${code}-${kind}`);
        setTimeout(() => setCopiedLink(''), 2000);
    };

    const getStatusBadge = (offer) => {
        const now = new Date();
        const expiresOn = new Date(offer.expiresOn);
        
        if (offer.currentUses >= offer.maxUses) {
            return (
                <span className="inline-flex px-2 py-1 text-xs font-display font-semibold rounded-full bg-luxury-graphite/50 text-accent-platinum/50 border border-emerald-600/20">
                    Used Up
                </span>
            );
        } else if (expiresOn < now) {
            return (
                <span className="inline-flex px-2 py-1 text-xs font-display font-semibold rounded-full bg-performance-pulse/20 text-performance-pulse border border-performance-pulse/30">
                    Expired
                </span>
            );
        } else {
            return (
                <span className="inline-flex px-2 py-1 text-xs font-display font-semibold rounded-full bg-emerald-600/20 text-emerald-300 border border-emerald-600/30">
                    Active
                </span>
            );
        }
    };

    const filteredOffers = offers.length > 0 ? offers.filter(offer => {
        const matchesSearch = 
            offer.recipientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            offer.recipientEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            offer.inviteCode?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const now = new Date();
        const expiresOn = new Date(offer.expiresOn);
        const isActive = offer.currentUses < offer.maxUses && expiresOn >= now;
        const isExpired = expiresOn < now;
        const isUsedUp = offer.currentUses >= offer.maxUses;

        const matchesFilter = 
            filterStatus === 'all' ||
            (filterStatus === 'active' && isActive) ||
            (filterStatus === 'expired' && isExpired) ||
            (filterStatus === 'used' && isUsedUp);

        return matchesSearch && matchesFilter;
    }) : [];
    
    if (loading && offers.length === 0) {
        return (
            <LayoutWrapper title="Subscription Offers" subtitle="Manage custom subscription invites">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper title="Subscription Offers" subtitle="Create and manage custom subscription invites for potential clients">
            {/* Header Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-600/60" />
                        <input
                            type="text"
                            placeholder="Search offers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input-field pl-10 w-full sm:min-w-[200px]"
                        />
                    </div>
                    
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="input-field w-full sm:min-w-[120px]"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="used">Used Up</option>
                    </select>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button
                        onClick={() => setShowExistingUserModal(true)}
                        className="btn-secondary flex items-center gap-2 w-full sm:w-auto"
                    >
                        <UserIcon className="h-5 w-5" />
                        <span>Offer to Existing User</span>
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn-primary flex items-center gap-2 w-full sm:w-auto"
                    >
                        <PlusIcon className="h-5 w-5" />
                        <span>Create Offer</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
                <div className="card-luxury p-4 sm:p-6 hover-lift">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                <GiftIcon className="h-5 w-5 text-white" />
                            </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                            <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">Total Offers</p>
                            <p className="text-lg sm:text-2xl font-display font-semibold text-accent-platinum">{offers.length}</p>
                        </div>
                    </div>
                </div>

                <div className="card-luxury p-4 sm:p-6 hover-lift">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                <CheckCircleIcon className="h-5 w-5 text-white" />
                            </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                            <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">Active Offers</p>
                            <p className="text-lg sm:text-2xl font-display font-semibold text-accent-platinum">
                                {filteredOffers.length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="card-luxury p-4 sm:p-6 hover-lift">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                <UserIcon className="h-5 w-5 text-white" />
                            </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                            <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">Total Uses</p>
                            <p className="text-lg sm:text-2xl font-display font-semibold text-accent-platinum">
                                {filteredOffers.reduce((sum, o) => sum + (o.currentUses || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Offers List */}
            {filteredOffers.length > 0 ? (
                <div className="space-y-4">
                    {filteredOffers.map((offer) => (
                        <div key={offer.id} className="card-luxury p-4 sm:p-6 hover-lift">
                            <div className="flex flex-col sm:flex-row justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        <h3 className="text-lg font-display font-semibold text-accent-platinum">
                                            {offer.subscriptionName || 'Custom Subscription'}
                                        </h3>
                                        {getStatusBadge(offer)}
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                        {offer.recipientName && (
                                            <div className="flex items-center gap-2">
                                                <UserIcon className="h-4 w-4 text-emerald-400" />
                                                <span className="text-emerald-600/70 font-body">Recipient:</span>
                                                <span className="text-accent-platinum font-body">{offer.recipientName}</span>
                                            </div>
                                        )}
                                        {offer.recipientEmail && (
                                            <div className="flex items-center gap-2">
                                                <EnvelopeIcon className="h-4 w-4 text-emerald-400" />
                                                <span className="text-emerald-600/70 font-body">Email:</span>
                                                <span className="text-accent-platinum font-body">{offer.recipientEmail}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2">
                                            <CalendarIcon className="h-4 w-4 text-emerald-400" />
                                            <span className="text-emerald-600/70 font-body">Expires:</span>
                                            <span className="text-accent-platinum font-body">
                                                {new Date(offer.expiresOn).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-emerald-600/70 font-body">Uses:</span>
                                            <span className="text-accent-platinum font-body">
                                                {offer.currentUses || 0} / {offer.maxUses}
                                            </span>
                                        </div>
                                    </div>

                                    {offer.notes && (
                                        <p className="mt-3 text-sm text-emerald-600/60 font-body italic">
                                            {offer.notes}
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2 sm:items-end">
                                    <div className="glass-luxury rounded-lg p-3 border border-emerald-600/20">
                                        <p className="text-xs text-emerald-600/70 font-body mb-1">Invite Code</p>
                                        <div className="flex items-center gap-2">
                                            <code className="text-sm font-mono text-emerald-300 font-semibold">
                                                {offer.inviteCode}
                                            </code>
                                            <button
                                                onClick={() => copyInviteCode(offer.inviteCode)}
                                                className="p-1 hover:bg-emerald-600/10 rounded transition-colors"
                                                title="Copy code"
                                            >
                                                <DocumentDuplicateIcon className="h-4 w-4 text-emerald-400" />
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            <button
                                                type="button"
                                                onClick={() => copyInviteLink(offer.inviteCode, 'signup')}
                                                className="px-3 py-1 text-xs font-display font-semibold rounded-lg bg-emerald-600/20 text-emerald-300 border border-emerald-600/30 hover:bg-emerald-600/30 transition-colors"
                                                title="Link for someone new to the venue. They create an account and set up payment."
                                            >
                                                {copiedLink === `${offer.inviteCode}-signup` ? 'Copied' : 'Copy sign up link'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => copyInviteLink(offer.inviteCode, 'member')}
                                                className="px-3 py-1 text-xs font-display font-semibold rounded-lg bg-emerald-600/10 text-emerald-300/80 border border-emerald-600/20 hover:bg-emerald-600/20 transition-colors"
                                                title="Link for someone who already has an account. They log in and accept."
                                            >
                                                {copiedLink === `${offer.inviteCode}-member` ? 'Copied' : 'Copy member link'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="card-luxury p-8 text-center">
                    <GiftIcon className="h-16 w-16 text-emerald-600/50 mx-auto mb-4" />
                    <h3 className="text-lg font-display font-medium text-accent-platinum mb-2">No offers found</h3>
                    <p className="text-emerald-600/70 font-body mb-4">
                        {searchTerm || filterStatus !== 'all'
                            ? 'No offers match your search criteria.'
                            : 'Create your first subscription offer to get started.'}
                    </p>
                    {!searchTerm && filterStatus === 'all' && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="btn-primary inline-flex items-center gap-2"
                        >
                            <PlusIcon className="h-5 w-5" />
                            <span>Create First Offer</span>
                        </button>
                    )}
                </div>
            )}

            {/* Create Offer Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="card-luxury p-6 sm:p-8 max-w-3xl w-full animate-slide-up my-8">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-display font-bold text-emerald-700 mb-2">
                                    Create Subscription Offer
                                </h2>
                                <p className="text-sm text-emerald-600/70 font-body">
                                    Choose an existing subscription or create a custom one
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setUseCustomSubscription(false);
                                }}
                                className="text-emerald-600/60 hover:text-emerald-300 transition-colors duration-300"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateOffer} className="space-y-6">
                            {/* Subscription Type Toggle */}
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setUseCustomSubscription(false)}
                                    className={`flex-1 py-3 px-4 rounded-xl font-display font-semibold transition-all ${
                                        !useCustomSubscription
                                            ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-600/40'
                                            : 'bg-luxury-graphite/50 text-accent-platinum/70 border border-emerald-600/20 hover:border-emerald-600/30'
                                    }`}
                                >
                                    Existing Subscription
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setUseCustomSubscription(true)}
                                    className={`flex-1 py-3 px-4 rounded-xl font-display font-semibold transition-all ${
                                        useCustomSubscription
                                            ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-600/40'
                                            : 'bg-luxury-graphite/50 text-accent-platinum/70 border border-emerald-600/20 hover:border-emerald-600/30'
                                    }`}
                                >
                                    Custom Subscription
                                </button>
                            </div>

                            {/* Existing Subscription Selection */}
                            {!useCustomSubscription && (
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                        <GiftIcon className="h-4 w-4" />
                                        Select Subscription Plan
                                    </label>
                                    <select
                                        required={!useCustomSubscription}
                                        value={formData.subscriptionId}
                                        onChange={(e) => setFormData({...formData, subscriptionId: e.target.value})}
                                        className="input-field w-full"
                                    >
                                        <option value="">Choose a subscription...</option>
                                        {subscriptions.filter(sub => !sub.isCustomerSubscription).map((sub) => (
                                            <option key={sub.subscriptionId} value={sub.id}>
                                                {sub.name} - {sub.tokensPerCycle} tokens - {sub.subscriptionCycleCost} {sub.subscriptionCycleCostCurrency} ({planCycleText(sub.subscriptionCycleType, sub.subscriptionCycleWeeks)})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Custom Subscription Fields */}
                            {useCustomSubscription && (
                                <div className="space-y-4 p-4 glass-luxury rounded-xl border border-emerald-600/20">
                                    <h3 className="text-lg font-display font-semibold text-emerald-300 mb-4">Custom Subscription Details</h3>
                                    
                                    <div>
                                        <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                            <GiftIcon className="h-4 w-4" />
                                            Subscription Name
                                        </label>
                                        <input
                                            type="text"
                                            required={useCustomSubscription}
                                            value={formData.customSubscriptionName}
                                            onChange={(e) => setFormData({...formData, customSubscriptionName: e.target.value})}
                                            className="input-field w-full"
                                            placeholder="e.g., Premium Package for John"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                                <CurrencyDollarIcon className="h-4 w-4" />
                                                Price
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                required={useCustomSubscription}
                                                value={formData.customPrice}
                                                onChange={(e) => setFormData({...formData, customPrice: e.target.value})}
                                                className="input-field w-full"
                                                placeholder="0.00"
                                            />
                                        </div>

                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                                <BanknotesIcon className="h-4 w-4" />
                                                Tokens
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                required={useCustomSubscription}
                                                value={formData.customTokenAmount}
                                                onChange={(e) => setFormData({...formData, customTokenAmount: e.target.value})}
                                                className="input-field w-full"
                                                placeholder="10"
                                            />
                                        </div>

                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                                <ArrowPathIcon className="h-4 w-4" />
                                                Frequency
                                            </label>
                                            <select
                                                required={useCustomSubscription}
                                                value={formData.customFrequency}
                                                onChange={(e) => setFormData({...formData, customFrequency: e.target.value})}
                                                className="input-field w-full"
                                            >
                                                <option value="Weekly">Weekly</option>
                                                <option value="Monthly">Monthly</option>
                                                <option value="Quarterly">Quarterly</option>
                                                <option value="Yearly">Yearly</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                                <ArrowPathIcon className="h-4 w-4" />
                                                Repeat every
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="52"
                                                required={useCustomSubscription}
                                                value={formData.customCycleLength}
                                                onChange={(e) => setFormData({...formData, customCycleLength: e.target.value})}
                                                className="input-field w-full"
                                            />
                                            <p className="text-xs text-emerald-600/60 mt-1">
                                                {describeCycle(formData.customFrequency, formData.customCycleLength)}
                                            </p>
                                        </div>

                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                                <CalendarIcon className="h-4 w-4" />
                                                First payment date (optional)
                                            </label>
                                            <input
                                                type="date"
                                                min={todayString()}
                                                value={formData.customFirstCollectionDate}
                                                onChange={(e) => setFormData({...formData, customFirstCollectionDate: e.target.value})}
                                                className="input-field w-full"
                                            />
                                            <p className="text-xs text-emerald-600/60 mt-1">
                                                Use a date from your normal direct debit run. Leave empty to let GoCardless choose.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Recipient Details */}
                            <p className="text-xs text-emerald-600/60 -mb-2">
                                Add their email so the link is made for them. If they already have a WorldsHighStreet profile they are
                                asked to log in instead of creating a new one, so nothing needs deleting.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                        <UserIcon className="h-4 w-4" />
                                        Recipient Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.recipientName}
                                        onChange={(e) => setFormData({...formData, recipientName: e.target.value})}
                                        className="input-field w-full"
                                        placeholder="Optional"
                                    />
                                </div>

                                <div>
                                    <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                        <EnvelopeIcon className="h-4 w-4" />
                                        Recipient Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.recipientEmail}
                                        onChange={(e) => setFormData({...formData, recipientEmail: e.target.value})}
                                        className="input-field w-full"
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>

                            {/* Offer Settings */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                        Max Uses
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        value={formData.maxUses}
                                        onChange={(e) => setFormData({...formData, maxUses: parseInt(e.target.value)})}
                                        className="input-field w-full"
                                    />
                                </div>

                                <div>
                                    <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                        <CalendarIcon className="h-4 w-4" />
                                        Expires On
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.expiresOn}
                                        onChange={(e) => setFormData({...formData, expiresOn: e.target.value})}
                                        className="input-field w-full"
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                                    Notes
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                    className="input-field w-full"
                                    rows="3"
                                    placeholder="Optional notes about this offer..."
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setUseCustomSubscription(false);
                                    }}
                                    className="btn-outline flex-1 order-2 sm:order-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary flex-1 order-1 sm:order-2"
                                >
                                    {loading ? 'Creating...' : 'Create Offer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Offer to Existing User Modal */}
            {showExistingUserModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="card-luxury p-6 sm:p-8 max-w-3xl w-full animate-slide-up my-8">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-display font-bold text-emerald-700 mb-2">
                                    Offer Subscription to Existing User
                                </h2>
                                <p className="text-sm text-emerald-600/70 font-body">
                                    Send a subscription offer to an existing user via email
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowExistingUserModal(false);
                                    setUseCustomForExisting(false);
                                }}
                                className="text-emerald-600/60 hover:text-emerald-300 transition-colors duration-300"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <form onSubmit={handleOfferToExistingUser} className="space-y-6">
                            {/* Existing User Selection Dropdown */}
                            <div>
                                <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                    Select Existing User
                                </label>
                                <select
                                    value={existingUserFormData.selectedUserId || ''}
                                    onChange={(e) => {
                                        const selectedUser = venueUsers.find(u => u.userId === e.target.value);
                                        if (selectedUser) {
                                            setExistingUserFormData({
                                                ...existingUserFormData,
                                                selectedUserId: selectedUser.userId,
                                                userEmail: selectedUser.email || selectedUser.userEmail || ''
                                            });
                                        } else {
                                            setExistingUserFormData({
                                                ...existingUserFormData,
                                                selectedUserId: '',
                                                userEmail: ''
                                            });
                                        }
                                    }}
                                    className="input-field w-full"
                                >
                                    <option value="">-- Select a user or enter email below --</option>
                                    {venueUsers.map(user => (
                                        <option key={user.userId} value={user.userId}>
                                            {user.name || user.userName || 'Unnamed User'} ({user.email || user.userEmail})
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-emerald-600/60 mt-1">
                                    Select from existing venue users or manually enter an email below
                                </p>
                            </div>

                            {/* User Email */}
                            <div>
                                <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                    User Email *
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={existingUserFormData.userEmail}
                                    onChange={(e) => setExistingUserFormData({...existingUserFormData, userEmail: e.target.value})}
                                    className="input-field w-full"
                                    placeholder="user@example.com"
                                />
                                <p className="text-xs text-emerald-600/60 mt-1">
                                    The user will receive an email with a link to accept the offer.
                                    This works for anyone who already has a WorldsHighStreet profile, even if they have never joined your venue.
                                    They log in, accept and set up their Direct Debit. Do not delete their profile.
                                </p>
                            </div>

                            {/* Subscription Type Toggle */}
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setUseCustomForExisting(false)}
                                    className={`flex-1 py-3 px-4 rounded-xl font-display font-semibold transition-all ${
                                        !useCustomForExisting
                                            ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-600/40'
                                            : 'bg-luxury-graphite/50 text-accent-platinum/70 border border-emerald-600/20 hover:border-emerald-600/30'
                                    }`}
                                >
                                    Existing Subscription
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setUseCustomForExisting(true)}
                                    className={`flex-1 py-3 px-4 rounded-xl font-display font-semibold transition-all ${
                                        useCustomForExisting
                                            ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-600/40'
                                            : 'bg-luxury-graphite/50 text-accent-platinum/70 border border-emerald-600/20 hover:border-emerald-600/30'
                                    }`}
                                >
                                    Custom Subscription
                                </button>
                            </div>

                            {/* Conditional Fields based on subscription type */}
                            {!useCustomForExisting ? (
                                <div>
                                    <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                        Select Subscription *
                                    </label>
                                    <select
                                        required
                                        value={existingUserFormData.subscriptionId}
                                        onChange={(e) => setExistingUserFormData({...existingUserFormData, subscriptionId: e.target.value})}
                                        className="input-field w-full"
                                    >
                                        <option value="">Choose a subscription...</option>
                                        {subscriptions.filter(sub => !sub.isCustomerSubscription).map(sub => (
                                            <option key={sub.subscriptionId} value={sub.id}>
                                                {sub.name} - £{sub.subscriptionCycleCost} {planCycleText(sub.subscriptionCycleType, sub.subscriptionCycleWeeks)} | ({sub.tokensPerCycle} tokens Per cycle)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                            Subscription Name *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={existingUserFormData.customSubscriptionName}
                                            onChange={(e) => setExistingUserFormData({...existingUserFormData, customSubscriptionName: e.target.value})}
                                            className="input-field w-full"
                                            placeholder="Premium Membership"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                                Price (£) *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                required
                                                value={existingUserFormData.customPrice}
                                                onChange={(e) => setExistingUserFormData({...existingUserFormData, customPrice: e.target.value})}
                                                className="input-field w-full"
                                                placeholder="29.99"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                                Tokens *
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                required
                                                value={existingUserFormData.customTokenAmount}
                                                onChange={(e) => setExistingUserFormData({...existingUserFormData, customTokenAmount: e.target.value})}
                                                className="input-field w-full"
                                                placeholder="10"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                                Frequency *
                                            </label>
                                            <select
                                                required
                                                value={existingUserFormData.customFrequency}
                                                onChange={(e) => setExistingUserFormData({...existingUserFormData, customFrequency: e.target.value})}
                                                className="input-field w-full"
                                            >
                                                <option value="Weekly">Weekly</option>
                                                <option value="Monthly">Monthly</option>
                                                <option value="Quarterly">Quarterly</option>
                                                <option value="Yearly">Yearly</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                                Repeat every *
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="52"
                                                required
                                                value={existingUserFormData.customCycleLength}
                                                onChange={(e) => setExistingUserFormData({...existingUserFormData, customCycleLength: e.target.value})}
                                                className="input-field w-full"
                                            />
                                            <p className="text-xs text-emerald-600/60 mt-1">
                                                {describeCycle(existingUserFormData.customFrequency, existingUserFormData.customCycleLength)}
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                                First payment date (optional)
                                            </label>
                                            <input
                                                type="date"
                                                min={todayString()}
                                                value={existingUserFormData.customFirstCollectionDate}
                                                onChange={(e) => setExistingUserFormData({...existingUserFormData, customFirstCollectionDate: e.target.value})}
                                                className="input-field w-full"
                                            />
                                            <p className="text-xs text-emerald-600/60 mt-1">
                                                Use a date from your normal direct debit run. Leave empty to let GoCardless choose.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Expiry Days */}
                            <div>
                                <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                    Expires in (days) *
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    required
                                    value={existingUserFormData.expiryDays}
                                    onChange={(e) => setExistingUserFormData({...existingUserFormData, expiryDays: e.target.value})}
                                    className="input-field w-full"
                                />
                                <p className="text-xs text-emerald-600/60 mt-1">
                                    The offer will expire after this many days
                                </p>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-display font-semibold text-emerald-700 mb-2">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    value={existingUserFormData.notes}
                                    onChange={(e) => setExistingUserFormData({...existingUserFormData, notes: e.target.value})}
                                    className="input-field w-full"
                                    rows={3}
                                    placeholder="Add any notes about this offer..."
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowExistingUserModal(false);
                                        setUseCustomForExisting(false);
                                    }}
                                    className="btn-secondary w-full sm:w-auto"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full sm:flex-1 flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <ArrowPathIcon className="h-5 w-5 animate-spin" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <EnvelopeIcon className="h-5 w-5" />
                                            <span>Send Offer</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </LayoutWrapper>
    );
};

export default SubscriptionOffers;
